import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';
import { formatPartnerRouteName } from '../../../common/formatter';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useUser from '../../../hooks/useUser';
import { setDashboardMobileSideMenuOpen } from '../../../redux/ducks/UIDucks';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Avatar from '../../layout/Avatar/Avatar';

const DashboardSideMenuUserLogo = () => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const dispatch = useDispatch();

  const onNavigateToRoute = (route: string) => {
    navigate(route);
    dispatch(setDashboardMobileSideMenuOpen(false));
  };

  const partnerRoute = useMemo<string>(() => {
    if (currentUser) {
      return toLocalizedRoute(
        ROUTES.PARTNER_PUBLIC_PROFILE_ROUTE.replace(
          ':partnerId',
          `${currentUser?.partner_id}`,
        ).replace(
          ':partnerName',
          formatPartnerRouteName(
            `${currentUser?.first_name} ${currentUser?.last_name}`,
          ),
        ),
      );
    }

    return '/';
  }, [currentUser, toLocalizedRoute]);

  return currentUser ? (
    <span>
      <div className="flex flex-row items-center justify-start gap-3 p-3">
        <button
          id="dashboard-side-menu-item-profile-avatar"
          key="dashboard-side-menu-item-profile-avatar"
          aria-label="dashboard-side-menu-item-profile-avatar"
          data-test-id="dashboard-side-menu-item-profile-avatar"
          data-umami-event="dashboard-side-menu-item-profile-avatar"
          disabled={false}
          onClick={() => onNavigateToRoute(partnerRoute)}
          type="button"
          className="cursor-pointer group"
        >
          <span className="relative flex aspect-square size-[48px] flex-col items-center justify-center rounded-full bg-slate-100 uppercase shadow group-hover:shadow-lg">
            {currentUser?.media?.profile_picture ? (
              <span className="absolute">
                <Avatar
                  avatarImage={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                    currentUser?.media?.profile_picture
                  }`}
                  size={48}
                  border="border border-white shadow group-hover:shadow-md"
                />
              </span>
            ) : (
              `${currentUser.first_name?.substring(
                0,
                1,
              )}${currentUser.last_name?.substring(0, 1)}`
            )}
          </span>
        </button>
        <span className="flex flex-col text-left items-start justify-center grow gap-0 text-white">
          <span className="capitalize line-clamp-1 overflow-hidden text-ellipsis text-xl font-bold drop-shadow group-hover:drop-shadow-md">{`${currentUser.first_name} ${currentUser.last_name}`}</span>
          {!isNil(currentUser) && (
            <span className="-mt-1 line-clamp-1 overflow-hidden text-ellipsis font-bold drop-shadow group-hover:drop-shadow-md text-sm cursor-pointer hover:underline">
              <Link
                to={toLocalizedRoute(ROUTES.PRICES_ROUTE)}
                className="cursor-pointer"
              >
                {t(`static.logo.account_tier_name_${currentUser.account_tier}`)}
              </Link>
            </span>
          )}
        </span>
      </div>
    </span>
  ) : null;
};

export default DashboardSideMenuUserLogo;
